import {
  applySnapshot,
  flow,
  getSnapshot,
  SnapshotOrInstance,
  types,
} from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

import { TagDictionaryItem } from "./dictionary/TagDictionary";

import { getConvertedDuration, reorder } from "utils/calculation";

import {
  ActiveProgram,
  WorkoutCategoryModel,
  PROGRAM_PERMISSION_TYPES,
  PUBLISH_STATUSES,
} from "constants/models";
import { REORDER_MOVE_TYPES } from "constants/reorder";

export const EquipmentModel = types.model({
  id: types.number,
  title: types.string,
  imageUrl: types.string,
});

export const MovementModel = types.model({
  id: types.number,
  workoutMovementInCategoryId: types.maybe(types.number),
  title: types.string,
  duration: types.maybe(types.number),
  imageUrl: types.string,
});

export const WorkoutModel = types
  .model({
    id: types.number,
    title: types.string,
    duration: types.maybe(types.number),
    imageUrl: types.maybe(types.string),
    order: types.maybe(types.number),
    label: types.maybe(types.string),
    day: types.maybe(types.number),
    created: types.maybe(types.string),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  })
  .views(self => ({
    get durationText() {
      return getConvertedDuration(self.duration);
    },
  }));

export const WorkoutDetailsModel = types
  .model({
    id: types.number,
    title: types.string,
    videoUrl: types.maybe(types.string),
    duration: types.maybe(types.number),
    instruction: types.maybe(types.string),
    imageUrl: types.maybe(types.string),
    activePrograms: types.array(ActiveProgram),
    categories: types.array(WorkoutCategoryModel),
    equipments: types.array(EquipmentModel),
    movements: types.array(MovementModel),
    tags: types.array(TagDictionaryItem),
  })
  .views(self => ({
    get accessFormData() {
      return {
        activePrograms: self.activePrograms.map(({ id, name, permission }) => ({
          id,
          name,
          switchState: permission === PROGRAM_PERMISSION_TYPES.PREVIEW,
        })),
      };
    },
  }));

const CategoryDetailsStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/workout/list`, params);
  }),
  itemModel: WorkoutModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(WorkoutDetailsModel),
    isMoreLoading: types.optional(types.boolean, false),
    isWorkoutLoading: types.optional(types.boolean, false),
    isDetailsManaging: types.optional(types.boolean, false),
    isWorkoutReordering: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return self.page !== self.totalPages;
    },
  }))
  .actions(self => ({
    getReorderInfo(
      reorderedItems: SnapshotOrInstance<typeof WorkoutCategoryModel>[],
      workoutId: number,
    ) {
      const workoutIdNewIndex = reorderedItems.findIndex(
        (item: SnapshotOrInstance<typeof WorkoutCategoryModel>) =>
          item.id === workoutId,
      );

      switch (workoutIdNewIndex) {
        case 0:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.TOP_OF_PAGE,
          };
        case self.items.length - 1:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.BOTTOM_OF_PAGE,
          };
        default:
          return {
            afterId: reorderedItems[workoutIdNewIndex - 1].id,
            moveType: REORDER_MOVE_TYPES.NORMAL,
          };
      }
    },
  }))
  .actions(self => ({
    fetchMoreWorkouts: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/workout/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
            search: self.search,
            ...self.filters,
          });

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    fetchWorkoutDetails: flow(function*(categoryId: number, workoutId: number) {
      self.isWorkoutLoading = true;

      try {
        const details = yield api.get(
          `/workout/category/${categoryId}/workout/${workoutId}`,
        );

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isWorkoutLoading = false;
      }
    }),

    addWorkoutToCategory: flow(function*(payload: any) {
      self.isDetailsManaging = true;

      try {
        return yield api.post(
          `/workout/category/${payload.categoryId}/add/workout`,
          payload,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isDetailsManaging = false;
      }
    }),

    removeWorkoutFromCategory: flow(function*(payload: any) {
      self.isDetailsManaging = true;

      try {
        return yield api.delete(
          `/workout/category/${payload.categoryId}/remove/workout`,
          payload,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isDetailsManaging = false;
      }
    }),

    updateWorkoutAccessPrograms: flow(function*(payload: any) {
      self.isDetailsManaging = true;

      try {
        const { categoryId, workoutId, ...data } = payload;

        yield api.put(
          `/workout/category/${categoryId}/workout/${workoutId}`,
          data,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isDetailsManaging = false;
      }
    }),

    reorderWorkouts: flow(function*({
      categoryId,
      startIndex,
      endIndex,
      dayOfWeek,
    }: {
      categoryId: number;
      startIndex: number;
      endIndex: number;
      dayOfWeek?: number;
    }) {
      self.isWorkoutReordering = true;
      const { items } = getSnapshot(self);

      try {
        if (dayOfWeek) {
          const itemsToReorder = items.filter(({ day }) => day === dayOfWeek);

          const workoutId = itemsToReorder[startIndex].id;
          const reorderedItemsGroup = reorder(
            itemsToReorder,
            startIndex,
            endIndex,
          );
          const reorderInfo = self.getReorderInfo(
            reorderedItemsGroup,
            workoutId,
          );

          const startIndexToReplace = items.findIndex(
            ({ day }) => day === dayOfWeek,
          );

          const reorderedItems = [...items];
          reorderedItems.splice(
            startIndexToReplace,
            reorderedItemsGroup.length,
            ...reorderedItemsGroup,
          );

          applySnapshot(self, { ...self, items: reorderedItems });

          yield api.post(`/workout/category/${categoryId}/workout/move`, {
            id: workoutId,
            ...reorderInfo,
            dayOfWeek: dayOfWeek,
          });
        } else {
          const workoutId = items[startIndex].id;
          const reorderedItems = reorder(items, startIndex, endIndex);
          applySnapshot(self, { ...self, items: reorderedItems });

          const reorderInfo = self.getReorderInfo(reorderedItems, workoutId);
          yield api.post(`/workout/category/${categoryId}/workout/move`, {
            id: workoutId,
            ...reorderInfo,
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        applySnapshot(self, { ...self, items });
        throw error;
      } finally {
        self.isWorkoutReordering = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export default CategoryDetailsStore.create();
